import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Per-bidder WATCHLIST — a bidder's shortlist of lots they intend to bid on but
 * haven't yet. Stored as a map on the bidder's own user doc: users/{uid}.watchlist,
 * keyed `${auctionId}_${lotId}`. This mirrors the existing users/{uid}.dismissedBids
 * pattern in MyBids, and `watchlist` is an owner-writable field per firestore.rules
 * (the users update rule only blocks authnet/role/paymentType*), so NO rules change
 * is needed. Persists across sessions by living on the user doc.
 */
export interface WatchItem {
  auctionId: string;
  lotId: string;
  lotNumber?: string; // denormalized for resilient display
  lotTitle?: string;
  addedAt: number;    // epoch ms
}

export const watchKey = (auctionId: string, lotId: string) => `${auctionId}_${lotId}`;

export async function getWatchlist(uid: string): Promise<Record<string, WatchItem>> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return {};
  return ((snap.data() as any).watchlist as Record<string, WatchItem>) || {};
}

export async function addWatch(uid: string, item: WatchItem): Promise<void> {
  const key = watchKey(item.auctionId, item.lotId);
  // Nested-object merge is safe even if the doc or `watchlist` field is absent.
  await setDoc(doc(db, 'users', uid), { watchlist: { [key]: item } }, { merge: true });
}

export async function removeWatch(uid: string, auctionId: string, lotId: string): Promise<void> {
  const key = watchKey(auctionId, lotId);
  await updateDoc(doc(db, 'users', uid), { [`watchlist.${key}`]: deleteField() });
}
